import React from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "info"
  | "warning"
  | "ghost"
  | "tab";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  active?: boolean; // For tab buttons
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  fullWidth = false,
  children,
  leftIcon,
  rightIcon,
  active = false,
  className = "",
  type = "button",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-950";

  // Size variants
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  // Color variants
  const variantClasses = {
    primary:
      "bg-amber-400 text-zinc-950 hover:bg-amber-300 focus:ring-amber-400 disabled:bg-amber-400/50 disabled:opacity-70",
    secondary: "bg-zinc-700 text-white hover:bg-zinc-600 focus:ring-zinc-500 disabled:opacity-50",
    success:
      "bg-emerald-600 text-white hover:bg-emerald-500 focus:ring-emerald-500 disabled:opacity-50",
    danger: "bg-red-600 text-white hover:bg-red-500 focus:ring-red-500 disabled:opacity-50",
    info: "bg-blue-600 text-white hover:bg-blue-500 focus:ring-blue-500 disabled:opacity-50",
    warning:
      "bg-amber-500 text-zinc-950 hover:bg-amber-400 focus:ring-amber-500 disabled:opacity-50",
    ghost:
      "bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white focus:ring-zinc-600 disabled:opacity-50",
    tab: active
      ? "text-amber-400 border-b-2 border-amber-400 bg-transparent"
      : "text-zinc-400 bg-transparent hover:text-zinc-200 focus:ring-amber-400",
  };

  // Full width class
  const widthClass = fullWidth ? "w-full" : "";

  // Disabled state
  const isDisabled = disabled || loading;

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const combinedClasses = [
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    widthClass,
    isDisabled ? "cursor-not-allowed" : "cursor-pointer",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={combinedClasses} disabled={isDisabled} {...props}>
      {loading && <LoadingSpinner />}
      {leftIcon && !loading && <span className="mr-2">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button;
